import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Route as RouteIcon, ArrowUp, ArrowDown, X, RefreshCw, Users, Truck, Send, Clock } from "lucide-react";
import { Card, Btn } from "@/components/admin/ui";
import { API } from "@/config";
import { fmtErr } from "@/context/AuthProvider";

/* ==================== Admin: Route Builder ================================
 * Assign the day's paid deliveries + pickups to specific drivers in a specific
 * order. Uses simple up/down/unassign buttons per row (drag-and-drop is a
 * later enhancement — buttons work reliably on mobile which is where dispatch
 * runs on race days).
 */
const KindBadge = ({ kind }) => {
  const cls = kind === "delivery"
    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200"
    : "bg-orange-500/15 border-orange-500/40 text-orange-200";
  return <span className={`text-[10px] font-medium uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>{kind}</span>;
};

export const RouteBuilder = () => {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [day, setDay] = useState(today);
  const [drivers, setDrivers] = useState([]);
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [etaState, setEtaState] = useState({}); // {[key]: {busy, showInput, customMin, notifiedAt}}

  const etaKey = (stop) => `${stop.reservation_id}-${stop.kind}`;

  const sendAdminETA = async (stop) => {
    const key = etaKey(stop);
    const custom = etaState[key]?.customMin;
    setEtaState((s) => ({ ...s, [key]: { ...s[key], busy: true } }));
    try {
      const body = { kind: stop.kind };
      if (custom) body.eta_override_min = parseInt(custom, 10);
      const { data } = await axios.post(`${API}/admin/reservations/${stop.reservation_id}/send-eta`, body);
      const src = data.eta_source === "manual" ? "manual ETA" : data.eta_source === "depot" ? "depot" : "estimate";
      toast.success(
        data.sms_sent
          ? `Customer notified · ETA ${data.eta_text || data.eta_min + " min"} (${src})`
          : `ETA recorded · No SMS consent (${src})`
      );
      setEtaState((s) => ({ ...s, [key]: { busy: false, showInput: false, customMin: "", notifiedAt: data.notified_at || new Date().toISOString() } }));
      setStops((prev) => prev.map((s) =>
        s.reservation_id === stop.reservation_id && s.kind === stop.kind
          ? { ...s, notified_at: data.notified_at || new Date().toISOString(), eta_min: data.eta_min }
          : s
      ));
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Send ETA failed");
      setEtaState((s) => ({ ...s, [key]: { ...s[key], busy: false } }));
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/route-board`, { params: { day } });
      setDrivers(data.drivers || []);
      setStops(data.stops || []);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Couldn't load route board");
    } finally {
      setLoading(false);
    }
  }, [day]);

  useEffect(() => { load(); }, [load]);

  // Update the local state, then persist.
  const assign = async (stop, driverId, sequence) => {
    try {
      await axios.post(`${API}/admin/reservations/${stop.reservation_id}/assign-driver`, {
        driver_id: driverId,
        kind: stop.kind,
        sequence,
      });
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Assign failed");
      load();
    }
  };

  const assignToDriver = async (stop, driverId) => {
    // Find the driver's existing max sequence + 1 and append.
    const existing = stops.filter(s => s.driver_id === driverId && s.kind === stop.kind && s.reservation_id !== stop.reservation_id);
    const nextSeq = existing.reduce((m, s) => Math.max(m, s.sequence || 0), 0) + 1;
    setStops(prev => prev.map(s =>
      s.reservation_id === stop.reservation_id && s.kind === stop.kind
        ? { ...s, driver_id: driverId, sequence: nextSeq }
        : s
    ));
    await assign(stop, driverId, nextSeq);
    toast.success(`Assigned to ${drivers.find(d => d.id === driverId)?.name || "driver"}`);
  };

  const unassign = async (stop) => {
    setStops(prev => prev.map(s =>
      s.reservation_id === stop.reservation_id && s.kind === stop.kind
        ? { ...s, driver_id: null, sequence: null }
        : s
    ));
    try {
      await axios.post(`${API}/admin/reservations/${stop.reservation_id}/assign-driver`, {
        driver_id: null,
        kind: stop.kind,
      });
      toast.success("Unassigned");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
      load();
    }
  };

  // Move a stop up/down within its driver's list, then persist the full order.
  const move = async (stop, delta) => {
    const bucket = stops
      .filter(s => s.driver_id === stop.driver_id && s.kind === stop.kind)
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    const idx = bucket.findIndex(s => s.reservation_id === stop.reservation_id);
    const j = idx + delta;
    if (j < 0 || j >= bucket.length) return;
    const swap = [...bucket];
    [swap[idx], swap[j]] = [swap[j], swap[idx]];
    // Re-sequence + optimistic update.
    const orderedIds = swap.map(s => s.reservation_id);
    setStops(prev => prev.map(s => {
      if (s.driver_id !== stop.driver_id || s.kind !== stop.kind) return s;
      const newIdx = orderedIds.indexOf(s.reservation_id);
      return newIdx >= 0 ? { ...s, sequence: newIdx + 1 } : s;
    }));
    try {
      await axios.post(`${API}/admin/routes/reorder`, {
        driver_id: stop.driver_id,
        kind: stop.kind,
        day,
        ordered_reservation_ids: orderedIds,
      });
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
      load();
    }
  };

  const unassigned = stops.filter(s => !s.driver_id);
  const byDriver = drivers.reduce((acc, d) => {
    const list = stops.filter(s => s.driver_id === d.id).sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    acc[d.id] = list;
    return acc;
  }, {});

  // Parse the start hour from a slot string like "8-9", "14-16".
  const slotStartHour = (slot) => {
    if (!slot) return 9999;
    const h = parseInt(String(slot).split("-")[0], 10);
    return isNaN(h) ? 9999 : h;
  };

  const autoSort = async (driverId) => {
    const list = byDriver[driverId] || [];
    if (list.length < 2) return;
    const sorted = [...list].sort((a, b) => {
      const td = slotStartHour(a.slot) - slotStartHour(b.slot);
      return td !== 0 ? td : (a.address || "").localeCompare(b.address || "");
    });
    const orderedIds = sorted.map(s => s.reservation_id);
    // Optimistic UI update.
    setStops(prev => prev.map(s => {
      if (s.driver_id !== driverId) return s;
      const idx = orderedIds.indexOf(s.reservation_id);
      return idx >= 0 ? { ...s, sequence: idx + 1 } : s;
    }));
    try {
      const kinds = [...new Set(sorted.map(s => s.kind))];
      for (const kind of kinds) {
        const ids = sorted.filter(s => s.kind === kind).map(s => s.reservation_id);
        if (!ids.length) continue;
        await axios.post(`${API}/admin/routes/reorder`, { driver_id: driverId, kind, day, ordered_reservation_ids: ids });
      }
      toast.success("Stops sorted by time slot");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Sort failed");
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="route-builder-page">
      <div className="flex items-center gap-3 flex-wrap">
        <RouteIcon className="w-6 h-6 text-[#FFD166] flex-shrink-0" />
        <h1 className="font-serif text-3xl">Route Builder</h1>
        <input
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm ml-auto"
          data-testid="route-builder-date"
        />
        <Btn onClick={load} variant="ghost" data-testid="route-builder-reload">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Reload
        </Btn>
      </div>

      <p className="text-sm text-[#B9C8D5] max-w-3xl">
        Assign the day's paid deliveries and pickups to a driver, then use the arrows to
        set the order they'll appear on the driver's phone. Drivers only see stops assigned
        to them and can hit "Send Notification" per stop to text the customer their live ETA.
      </p>

      {/* Unassigned bucket ------------------------------------------------- */}
      <Card className="p-4" data-testid="route-builder-unassigned">
        <div className="flex items-center gap-2 mb-3">
          <Truck className="w-4 h-4 text-[#F4A93A]" />
          <h2 className="font-serif text-lg">Unassigned ({unassigned.length})</h2>
        </div>
        {unassigned.length === 0 ? (
          <div className="text-xs text-[#B9C8D5] italic">Every stop for {day} is assigned. ✨</div>
        ) : (
          <div className="space-y-2">
            {unassigned.map(s => (
              <div key={`${s.reservation_id}-${s.kind}`} className="p-3 rounded-lg border border-[#FFD166]/15 bg-black/25 flex items-center gap-3 flex-wrap" data-testid={`unassigned-${s.reservation_id}-${s.kind}`}>
                <KindBadge kind={s.kind} />
                <span className="text-xs text-[#B9C8D5]">{s.slot_label || s.slot}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#F6F1E4] truncate">{s.customer_name}</div>
                  <div className="text-[11px] text-[#B9C8D5] truncate">{s.address}</div>
                </div>
                <select
                  onChange={(e) => e.target.value && assignToDriver(s, e.target.value)}
                  defaultValue=""
                  className="bg-black/40 border border-[#FFD166]/25 text-[#F6F1E4] px-2 py-1.5 rounded text-xs"
                  data-testid={`assign-driver-${s.reservation_id}-${s.kind}`}
                >
                  <option value="" disabled>Assign to driver…</option>
                  {drivers.map(d => (
                    <option key={d.id} value={d.id}>{d.name || d.email}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Per-driver lists -------------------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {drivers.map(d => {
          const list = byDriver[d.id] || [];
          return (
            <Card key={d.id} className="p-4" data-testid={`driver-route-${d.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-[#FFD166]" />
                <h3 className="font-serif text-lg">{d.name || d.email}</h3>
                <span className="text-xs text-[#B9C8D5] ml-auto">{list.length} stop{list.length !== 1 ? "s" : ""}</span>
                {list.length > 1 && (
                  <button
                    onClick={() => autoSort(d.id)}
                    title="Sort by time slot"
                    className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-[#FFD166]/30 text-[#FFD166]/70 hover:text-[#FFD166] hover:border-[#FFD166]/60 transition-colors"
                    data-testid={`auto-sort-${d.id}`}
                  >
                    <Zap className="w-3 h-3" />Sort
                  </button>
                )}
              </div>
              {list.length === 0 ? (
                <div className="text-xs text-[#B9C8D5] italic">No stops yet — assign from the Unassigned list above.</div>
              ) : (
                <div className="space-y-2">
                  {list.map((s, i) => (
                    <div key={`${s.reservation_id}-${s.kind}`} className="p-3 rounded-lg border border-[#FFD166]/15 bg-black/25 flex items-center gap-2" data-testid={`driver-stop-${d.id}-${s.reservation_id}-${s.kind}`}>
                      <div className="w-7 h-7 rounded-full bg-[#FFD166]/15 border border-[#FFD166]/40 text-[#FFD166] flex items-center justify-center text-xs font-serif flex-shrink-0">{s.sequence || (i + 1)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <KindBadge kind={s.kind} />
                          <span className="text-[10px] text-[#B9C8D5]">{s.slot_label || s.slot}</span>
                          {s.notified_at && <span className="text-[10px] text-[#FFD166]">📣 Notified</span>}
                          {s.delivered_at && <span className="text-[10px] text-emerald-300">✓ Delivered</span>}
                        </div>
                        <div className="text-sm text-[#F6F1E4] truncate mt-0.5">{s.customer_name}</div>
                        <div className="text-[11px] text-[#B9C8D5] truncate">{s.address}</div>
                        {/* Send ETA inline */}
                        {etaState[etaKey(s)]?.showInput && (
                          <div className="mt-2 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <span className="text-[11px] text-[#B9C8D5]">ETA (min):</span>
                            <input
                              type="number"
                              min="1"
                              max="120"
                              placeholder="auto"
                              value={etaState[etaKey(s)]?.customMin || ""}
                              onChange={(e) => setEtaState((st) => ({ ...st, [etaKey(s)]: { ...st[etaKey(s)], customMin: e.target.value } }))}
                              className="w-16 bg-black/40 border border-[#FFD166]/30 text-[#F6F1E4] px-2 py-0.5 rounded text-xs"
                              data-testid={`eta-input-${s.reservation_id}-${s.kind}`}
                            />
                            <button
                              onClick={() => sendAdminETA(s)}
                              disabled={etaState[etaKey(s)]?.busy}
                              className="text-[11px] px-2 py-0.5 rounded bg-[#FFD166]/20 border border-[#FFD166]/40 text-[#FFD166] hover:bg-[#FFD166]/30 disabled:opacity-50"
                              data-testid={`eta-send-${s.reservation_id}-${s.kind}`}
                            >
                              {etaState[etaKey(s)]?.busy ? "…" : "Send"}
                            </button>
                            <button
                              onClick={() => setEtaState((st) => ({ ...st, [etaKey(s)]: { ...st[etaKey(s)], showInput: false } }))}
                              className="text-[11px] text-[#B9C8D5] hover:text-[#F6F1E4]"
                            >cancel</button>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEtaState((st) => ({ ...st, [etaKey(s)]: { ...st[etaKey(s)], showInput: !st[etaKey(s)]?.showInput } }))}
                          className="p-1.5 rounded text-[#B9C8D5] hover:text-[#FFD166] hover:bg-white/5"
                          title={s.notified_at ? "Resend ETA notification" : "Send ETA to customer"}
                          data-testid={`send-eta-toggle-${s.reservation_id}-${s.kind}`}
                        >
                          <Send className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => move(s, -1)} disabled={i === 0} className="p-1.5 rounded text-[#B9C8D5] hover:text-[#FFD166] hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`move-up-${s.reservation_id}-${s.kind}`}>
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => move(s, +1)} disabled={i === list.length - 1} className="p-1.5 rounded text-[#B9C8D5] hover:text-[#FFD166] hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`move-down-${s.reservation_id}-${s.kind}`}>
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => unassign(s)} className="p-1.5 rounded text-red-300 hover:bg-red-500/10" data-testid={`unassign-${s.reservation_id}-${s.kind}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {drivers.length === 0 && !loading && (
        <Card className="p-6 text-center text-[#B9C8D5]" data-testid="route-builder-no-drivers">
          You don't have any drivers yet. Invite one from the Drivers tab first.
        </Card>
      )}
    </div>
  );
};

export default RouteBuilder;
